import { useEffect, useRef, useState, useCallback } from "react";

// Two-row piano layout:
// Lower row (Z-M): one octave starting at baseOctave
// Upper row (Q-U): one octave starting at baseOctave + 1
const LOWER_ROW = {
  z: 0, s: 1, x: 2, d: 3, c: 4, v: 5,
  g: 6, b: 7, h: 8, n: 9, j: 10, m: 11,
};
const UPPER_ROW = {
  q: 12, "2": 13, w: 14, "3": 15, e: 16, r: 17,
  "5": 18, t: 19, "6": 20, y: 21, "7": 22, u: 23,
};

/**
 * @param {function} triggerNote  - called on keydown with (midi)
 * @param {boolean}  active       - only listen when true
 * @param {function} [onNoteOff]  - called on keyup with (midi)
 */
export default function useKeyboardNotes(triggerNote, active, onNoteOff) {
  const [baseOctave, setBaseOctave] = useState(3);
  const pressedRef = useRef(new Set());

  const getKeyMap = useCallback(() => {
    return { ...LOWER_ROW, ...UPPER_ROW };
  }, []);

  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.repeat) return;

      // Octave shift
      if (e.key === "[") {
        setBaseOctave((prev) => Math.max(1, prev - 1));
        return;
      }
      if (e.key === "]") {
        setBaseOctave((prev) => Math.min(6, prev + 1));
        return;
      }

      const keyMap = { ...LOWER_ROW, ...UPPER_ROW };
      const offset = keyMap[e.key.toLowerCase()];
      if (offset === undefined) return;

      e.preventDefault();
      const midi = (baseOctave + 1) * 12 + offset; // +1 because MIDI octave -1 starts at 0
      if (pressedRef.current.has(e.key)) return;
      pressedRef.current.add(e.key);
      triggerNote(midi);
    };

    const handleKeyUp = (e) => {
      if (!pressedRef.current.has(e.key)) return;
      pressedRef.current.delete(e.key);

      // Compute midi for the released key
      const keyMap = { ...LOWER_ROW, ...UPPER_ROW };
      const offset = keyMap[e.key.toLowerCase()];
      if (offset !== undefined && onNoteOff) {
        const midi = (baseOctave + 1) * 12 + offset;
        onNoteOff(midi);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [active, baseOctave, triggerNote, onNoteOff]);

  return { baseOctave, setBaseOctave, getKeyMap };
}
